import { lazy, Suspense } from 'react';
import { cn } from '@kognitos/lattice';

import { OutputRenderer } from '../OutputRenderer';
// Lazy: a static import would drag the syntax highlighter (and lattice's `ds`) into every preview surface's module graph, breaking partial-mock tests.
const CodeRenderer = lazy(() =>
  import('@/shared/components/CodeRenderer').then(m => ({
    default: m.CodeRenderer,
  })),
);
import type { TOutputModalLayout } from '../output-presentation';
import {
  EOutputSourceView,
  ERenderMode,
  type TResolvedValue,
} from '../../types';
import { isDownloadable, isHtmlString } from '../../utils';
import { EXPANDED_OUTPUT_SHEET_CLASSNAME } from '../renderers/expanded-output-sheet';

interface IPreviewContentProps {
  value: TResolvedValue | null;
  onClose: () => void;
  outputName?: string;
  taskDescription?: string | string[];
  onDownload?: (value: TResolvedValue, outputName: string) => void;
  scrollParent?: HTMLElement | null;
  layout?: TOutputModalLayout;
  sourceView?: EOutputSourceView;
  /** Raw text for `sourceView`, resolved by the caller (`PreviewLayover`) so a
   * remote/base64-backed CSV is fetched and decoded only once. */
  sourceText?: string | null;
}

export function PreviewContent({
  value,
  onClose,
  outputName,
  onDownload,
  scrollParent,
  layout = 'wide',
  sourceView = EOutputSourceView.PREVIEW,
  sourceText = null,
}: IPreviewContentProps) {
  const handleDownload =
    onDownload && value && isDownloadable(value)
      ? () => onDownload(value, outputName ?? '')
      : undefined;
  const displayedSourceText =
    sourceView === EOutputSourceView.SOURCE ? sourceText : null;

  return (
    <div
      className={cn(
        'output-modal-renderer w-full min-h-full',
        `output-modal-renderer--${layout}`,
        // Auto margins, not align-items: tall content would be centred above the scroll top.
        layout === 'centered'
          ? 'flex justify-start [&>*]:my-auto'
          : 'h-full p-0',
      )}
    >
      {displayedSourceText !== null ? (
        <Suspense fallback={null}>
          <CodeRenderer
            code={displayedSourceText}
            language={isHtmlString(value) ? 'html' : 'text'}
            wrapLines={false}
            variant="output"
            className={cn(
              EXPANDED_OUTPUT_SHEET_CLASSNAME,
              'whitespace-pre overflow-auto',
            )}
          />
        </Suspense>
      ) : (
        <OutputRenderer
          value={value}
          mode={ERenderMode.EXPANDED}
          onExpandedClose={onClose}
          outputName={outputName}
          onDownload={handleDownload}
          scrollParent={scrollParent}
        />
      )}
    </div>
  );
}
